import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  Timestamp,
  startAfter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { Client, ClientFormData, ClientSearchFilters, ApiResponse, PaginationInfo, PropertyType, ListingType } from '@/types';
import { clientFormSchema } from '@/utils/validationSchemas';
import { AuthService } from './auth';
import { MatchingService } from './matchingService';
import { UserRole, Permission } from '@/types';
import { FirestoreQueryBuilder } from '@/utils/firestoreQueryBuilder';

export class ClientService {
  private static collection = 'clients';

  // Create a new client with validation (supports full profile including address)
  static async createClient(clientData: ClientFormData, agentId: string): Promise<Client> {
    try {
      // Validate form data using Yup schema
      await clientFormSchema.validate(clientData, { abortEarly: false });

      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to create clients');
      }

      if (!AuthService.hasPermission(currentUser, Permission.WRITE_CLIENTS)) {
        throw new Error('User does not have permission to create clients');
      }

      if (currentUser.role !== UserRole.AGENT && currentUser.role !== UserRole.ADMIN) {
        throw new Error('Only agents and admins can create clients');
      }

      // Enforce agent assignment rules
      let finalAgentId = agentId;
      
      // If user is an agent, they can only create clients for themselves
      if (currentUser.role === UserRole.AGENT) {
        if (agentId && agentId !== currentUser.id) {
          throw new Error('Agents can only assign clients to themselves');
        }
        finalAgentId = currentUser.id;
      }
      
      // Fallback to current user ID if no agent ID provided (for admins)
      if (!finalAgentId) {
        finalAgentId = currentUser.id;
      }

      const now = Timestamp.now();
      const client: Omit<Client, 'id'> = {
        profile: clientData.profile,
        preferences: clientData.preferences,
        matchingProfile: {
          ...clientData.matchingProfile,
          preferences: clientData.preferences,
        },
        interactions: [],
        agentId: finalAgentId,
        source: clientData.source,
        status: clientData.status,
        tags: clientData.tags || [],
        notes: clientData.notes || '',
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(collection(db, this.collection), client);
      const createdClient = { ...client, id: docRef.id } as Client;

      // Trigger initial match generation
      try {
        await MatchingService.generateMatchesForClient(createdClient);
      } catch (matchError) {
        console.error('Failed to generate initial matches:', matchError);
        // Continue execution - client creation was successful
      }

      return createdClient;
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        const validationErrors = error.errors.join(', ');
        throw new Error(`Validation failed: ${validationErrors}`);
      }
      throw new Error(`Failed to create client: ${error.message || error}`);
    }
  }

  // Get client by ID
  static async getClient(id: string): Promise<Client | null> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to view clients');
      }

      if (!AuthService.hasPermission(currentUser, Permission.READ_CLIENTS)) {
        throw new Error('User does not have permission to read clients');
      }

      const docRef = doc(db, this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const client = { id: docSnap.id, ...docSnap.data() } as Client;

        // RBAC: Ensure only Admins or the assigned Agent can view the client record
        const isAdmin = currentUser.role === UserRole.ADMIN;
        const isAssignedAgent = currentUser.role === UserRole.AGENT && client.agentId === currentUser.id;
        
        if (!isAdmin && !isAssignedAgent) {
          throw new Error('You do not have permission to view this client');
        }

        // Fetch match history
        try {
          const matches = await MatchingService.getMatchesForClient(id);
          (client as any).matches = matches;
        } catch (error) {
          console.warn('Failed to fetch matches for client:', error);
          (client as any).matches = [];
        }

        return client;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to get client: ${error.message || error}`);
    }
  }

  // Get client by ID with real-time listener
  static subscribeToClient(
    id: string,
    callback: (client: Client | null) => void
  ): Unsubscribe {
    const docRef = doc(db, this.collection, id);
    
    return onSnapshot(
      docRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          const client = { id: docSnap.id, ...docSnap.data() } as Client;
          
          try {
            const currentUser = await AuthService.getCurrentUser();
            
            if (!currentUser) {
              console.error('User must be authenticated to view clients');
              callback(null);
              return;
            }

            if (!AuthService.hasPermission(currentUser, Permission.READ_CLIENTS)) {
              console.error('User does not have permission to read clients');
              callback(null);
              return;
            }

            // RBAC: Ensure only Admins or the assigned Agent can view the client record
            const isAdmin = currentUser.role === UserRole.ADMIN;
            const isAssignedAgent = currentUser.role === UserRole.AGENT && client.agentId === currentUser.id;
            
            if (!isAdmin && !isAssignedAgent) {
              console.error('You do not have permission to view this client');
              callback(null);
              return;
            }

            callback(client);
          } catch (error) {
            console.error('Error checking permissions in subscribeToClient:', error);
            callback(null);
          }
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error listening to client:', error);
        callback(null);
      }
    );
  }

  // Get all clients with pagination and filters
  static async getClients(
    filters: ClientSearchFilters = {},
    page: number = 1,
    pageSize: number = 12
  ): Promise<ApiResponse<Client[]>> {
    try {
      let q = query(collection(db, this.collection));

      // Apply filters
      if (filters.status && filters.status.length > 0) {
        q = query(q, where('status', 'in', filters.status));
      }

      if (filters.source && filters.source.length > 0) {
        q = query(q, where('source', 'in', filters.source));
      }

      if (filters.agentId) {
        q = query(q, where('agentId', '==', filters.agentId));
      }

      if (filters.tags && filters.tags.length > 0) {
        q = query(q, where('tags', 'array-contains-any', filters.tags));
      }

      // Order by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'));

      // Apply pagination
      q = query(q, limit(pageSize));

      const querySnapshot = await getDocs(q);
      const clients = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];

      // Calculate pagination info
      const totalItems = clients.length; // In production, you'd get this from a separate count query
      const totalPages = Math.ceil(totalItems / pageSize);
      const hasNext = page < totalPages;
      const hasPrev = page > 1;

      const pagination: PaginationInfo = {
        page,
        limit: pageSize,
        total: totalItems,
        totalPages,
        hasNext,
        hasPrev,
      };

      return {
        success: true,
        data: clients,
        pagination,
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to get clients: ${error}`,
      };
    }
  }

  // List clients with cursor-based pagination, filtering, search, and sorting
  static async listClients(
    filters: ClientSearchFilters = {},
    pageSize: number = 12,
    lastDoc?: QueryDocumentSnapshot,
    sortBy: 'createdAt' | 'updatedAt' | 'profile.firstName' | 'profile.lastName' = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<{ clients: Client[]; lastDoc: QueryDocumentSnapshot | null; hasMore: boolean }> {
    try {
      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to list clients');
      }

      if (!AuthService.hasPermission(currentUser, Permission.READ_CLIENTS)) {
        throw new Error('User does not have permission to read clients');
      }

      const baseQuery = collection(db, this.collection);
      const qb = new FirestoreQueryBuilder(baseQuery);

      // Agent-scoped queries: automatically filter by agentId if user is an agent
      if (currentUser.role === UserRole.AGENT) {
        // If filters.agentId is provided and different from current user, check if admin
        if (filters.agentId && filters.agentId !== currentUser.id) {
          throw new Error('Agents can only view their own clients');
        } else {
          // Auto-scope to current agent
          qb.where('agentId', '==', currentUser.id);
        }
      } else if (filters.agentId) {
        // Admin can filter by any agent
        qb.where('agentId', '==', filters.agentId);
      }

      // Apply status filters
      if (filters.status && filters.status.length > 0) {
        qb.where('status', 'in', filters.status);
      }

      // Apply source filters
      if (filters.source && filters.source.length > 0) {
        qb.where('source', 'in', filters.source);
      }

      // Apply tag filters
      if (filters.tags && filters.tags.length > 0) {
        qb.where('tags', 'array-contains-any', filters.tags);
      }

      // Apply date range filter
      if (filters.dateRange) {
        qb.addRangeFilter('createdAt', {
          start: filters.dateRange.start,
          end: filters.dateRange.end
        });
        
        // When filtering by range on a field, the first orderBy must be on that same field
        if (sortBy !== 'createdAt') {
          console.warn('Sorting by field other than createdAt is not supported when filtering by date range. Defaulting to createdAt.');
        }
        qb.orderBy('createdAt', sortOrder);
      } else {
        // Apply sorting
        qb.orderBy(sortBy, sortOrder);
      }

      // Apply pagination (fetch one extra to check if there are more)
      qb.paginate(pageSize + 1, lastDoc);

      const querySnapshot = await getDocs(qb.build());
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      // Remove the extra doc if we fetched one more
      let clients = (hasMore ? docs.slice(0, pageSize) : docs).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];

      // Apply client-side search filtering if searchTerm is provided
      if (filters.searchTerm && filters.searchTerm.trim()) {
        const searchLower = filters.searchTerm.toLowerCase().trim();
        clients = clients.filter(client => {
          const firstName = client.profile.firstName?.toLowerCase() || '';
          const lastName = client.profile.lastName?.toLowerCase() || '';
          const email = client.profile.email?.toLowerCase() || '';
          const fullName = `${firstName} ${lastName}`.trim();
          
          return (
            firstName.startsWith(searchLower) ||
            lastName.startsWith(searchLower) ||
            fullName.includes(searchLower) ||
            email.includes(searchLower)
          );
        });
      }

      // Filter by preferences if provided
      if (filters.preferences) {
        clients = clients.filter(client => {
          const prefs = client.preferences;
          const filterPrefs = filters.preferences!;

          // Check property types
          if (filterPrefs.propertyTypes && filterPrefs.propertyTypes.length > 0) {
            const hasMatchingType = filterPrefs.propertyTypes.some((type: PropertyType) =>
              prefs.propertyTypes.includes(type)
            );
            if (!hasMatchingType) return false;
          }

          // Check listing types
          if (filterPrefs.listingTypes && filterPrefs.listingTypes.length > 0) {
            const hasMatchingType = filterPrefs.listingTypes.some((type: ListingType) =>
              prefs.listingTypes.includes(type)
            );
            if (!hasMatchingType) return false;
          }

          // Check price range
          if (filterPrefs.priceRange) {
            const clientPriceRange = prefs.priceRange;
            const filterPriceRange = filterPrefs.priceRange;
            if (clientPriceRange.max < filterPriceRange.min || clientPriceRange.min > filterPriceRange.max) {
              return false;
            }
          }

          // Check location preferences
          if (filterPrefs.location?.cities && filterPrefs.location.cities.length > 0) {
            const hasMatchingCity = filterPrefs.location.cities.some((city: string) =>
              prefs.location.cities.includes(city)
            );
            if (!hasMatchingCity) return false;
          }

          return true;
        });
      }

      // The last document to be used as cursor for the next page
      // Note: We use the cursor of the last FETCHED document (before client-side filtering)
      // to ensure we continue traversing the DB correctly on the next call.
      const lastFetchedDoc = docs.length > 0 ? (hasMore ? docs[pageSize - 1] : docs[docs.length - 1]) : null;

      return {
        clients,
        lastDoc: lastFetchedDoc || null,
        hasMore,
      };
    } catch (error: any) {
      throw new Error(`Failed to list clients: ${error.message || error}`);
    }
  }

  // Update client with permission validation
  static async updateClient(id: string, updates: Partial<ClientFormData>): Promise<void> {
    try {
      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to update clients');
      }

      if (!AuthService.hasPermission(currentUser, Permission.WRITE_CLIENTS)) {
        throw new Error('User does not have permission to update clients');
      }

      // Get existing client to check ownership
      const client = await this.getClient(id);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check if user is the client's agent or admin
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only update your own clients');
      }

      // Validate updates if they include profile or preferences
      if (updates.profile || updates.preferences || updates.matchingProfile) {
        const existingData: ClientFormData = {
          profile: client.profile,
          preferences: client.preferences,
          matchingProfile: {
            behavior: client.matchingProfile.behavior,
            communication: client.matchingProfile.communication,
            priority: client.matchingProfile.priority,
          },
          source: client.source,
          status: client.status,
          tags: client.tags,
          notes: client.notes,
        };

        const updatedData = { ...existingData, ...updates };
        await clientFormSchema.validate(updatedData, { abortEarly: false });
      }

      const docRef = doc(db, this.collection, id);
      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      if (updates.profile) {
        updateData.profile = updates.profile;
      }
      if (updates.preferences) {
        updateData.preferences = updates.preferences;
        // Also update matching profile preferences
        updateData['matchingProfile.preferences'] = updates.preferences;
      }
      if (updates.matchingProfile) {
        updateData.matchingProfile = {
          ...client.matchingProfile,
          ...updates.matchingProfile,
          preferences: updates.preferences || client.preferences,
        };
      }
      if (updates.source) {
        updateData.source = updates.source;
      }
      if (updates.status) {
        updateData.status = updates.status;
      }
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }
      if (updates.notes !== undefined) {
        updateData.notes = updates.notes;
      }

      await updateDoc(docRef, updateData);

      // Trigger match recalculation if preferences changed
      if (updates.preferences || updates.matchingProfile) {
        try {
          // Construct the full client object for matching
          // We merge the existing client with the updates
          const updatedClient: Client = {
            ...client,
            profile: updates.profile || client.profile,
            preferences: updates.preferences || client.preferences,
            matchingProfile: updates.matchingProfile ? {
              ...client.matchingProfile,
              ...updates.matchingProfile,
              preferences: updates.preferences || client.preferences
            } : {
              ...client.matchingProfile,
              preferences: updates.preferences || client.preferences
            },
            source: updates.source || client.source,
            status: updates.status || client.status,
            tags: updates.tags || client.tags,
            notes: updates.notes || client.notes,
            updatedAt: updateData.updatedAt
          };
          
          await MatchingService.generateMatchesForClient(updatedClient);
        } catch (matchError) {
          console.error('Failed to recalculate matches:', matchError);
          // We don't throw here to ensure the update itself remains successful
        }
      }
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        const validationErrors = error.errors.join(', ');
        throw new Error(`Validation failed: ${validationErrors}`);
      }
      throw new Error(`Failed to update client: ${error.message || error}`);
    }
  }

  // Delete client with soft delete option and permission validation
  static async deleteClient(id: string, hardDelete: boolean = false): Promise<void> {
    try {
      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to delete clients');
      }

      if (!AuthService.hasPermission(currentUser, Permission.DELETE_CLIENTS)) {
        throw new Error('User does not have permission to delete clients');
      }

      // Get existing client
      const client = await this.getClient(id);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check if user is the client's agent or admin
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only delete your own clients');
      }

      if (hardDelete) {
        // Hard delete: Remove document
        const docRef = doc(db, this.collection, id);
        // TODO: Delete related matches (will be handled by Cloud Function)
        await deleteDoc(docRef);
      } else {
        // Soft delete: Update status to INACTIVE
        const docRef = doc(db, this.collection, id);
        await updateDoc(docRef, {
          status: 'inactive' as any,
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error: any) {
      throw new Error(`Failed to delete client: ${error.message || error}`);
    }
  }

  // Get client by email
  static async getClientByEmail(email: string): Promise<Client | null> {
    try {
      const q = query(
        collection(db, this.collection),
        where('profile.email', '==', email),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as Client;
      }
      return null;
    } catch (error) {
      throw new Error(`Failed to get client by email: ${error}`);
    }
  }

  // Get clients by agent
  static async getClientsByAgent(agentId: string): Promise<Client[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('agentId', '==', agentId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
    } catch (error) {
      throw new Error(`Failed to get clients by agent: ${error}`);
    }
  }

  // Search clients by text
  static async searchClients(searchTerm: string): Promise<Client[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // In production, you'd use Algolia, Elasticsearch, or Cloud Functions
      const q = query(
        collection(db, this.collection),
        where('profile.firstName', '>=', searchTerm),
        where('profile.firstName', '<=', searchTerm + '\uf8ff'),
        orderBy('profile.firstName')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
    } catch (error) {
      throw new Error(`Failed to search clients: ${error}`);
    }
  }

  // Add client interaction
  static async addClientInteraction(
    clientId: string,
    interaction: Omit<Client['interactions'][0], 'id' | 'timestamp'>
  ): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to add interactions');
      }

      const client = await this.getClient(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check permissions
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only add interactions for your own clients');
      }

      const newInteraction: Client['interactions'][0] = {
        ...interaction,
        id: `interaction_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Timestamp.now(),
        agentId: currentUser.id,
      };

      await updateDoc(doc(db, this.collection, clientId), {
        interactions: [...(client.interactions || []), newInteraction],
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to add client interaction: ${error.message || error}`);
    }
  }

  // Update client interaction
  static async updateClientInteraction(
    clientId: string,
    interactionId: string,
    updates: Partial<Omit<Client['interactions'][0], 'id' | 'timestamp' | 'agentId'>>
  ): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to update interactions');
      }

      const client = await this.getClient(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check permissions
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only update interactions for your own clients');
      }

      const updatedInteractions = (client.interactions || []).map((interaction) => {
        if (interaction.id === interactionId) {
          return {
            ...interaction,
            ...updates,
          };
        }
        return interaction;
      });

      await updateDoc(doc(db, this.collection, clientId), {
        interactions: updatedInteractions,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to update client interaction: ${error.message || error}`);
    }
  }

  // Delete client interaction
  static async deleteClientInteraction(clientId: string, interactionId: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to delete interactions');
      }

      const client = await this.getClient(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check permissions
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only delete interactions for your own clients');
      }

      const filteredInteractions = (client.interactions || []).filter(
        (interaction) => interaction.id !== interactionId
      );

      await updateDoc(doc(db, this.collection, clientId), {
        interactions: filteredInteractions,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to delete client interaction: ${error.message || error}`);
    }
  }

  // Get client interactions
  static async getClientInteractions(clientId: string): Promise<Client['interactions']> {
    try {
      const client = await this.getClient(clientId);
      return client?.interactions || [];
    } catch (error: any) {
      throw new Error(`Failed to get client interactions: ${error.message || error}`);
    }
  }

  // Add client note
  static async addClientNote(clientId: string, note: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to add notes');
      }

      const client = await this.getClient(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check permissions
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only add notes for your own clients');
      }

      const updatedNotes = client.notes
        ? `${client.notes}\n\n[${new Date().toLocaleString()}] ${note}`
        : `[${new Date().toLocaleString()}] ${note}`;

      await updateDoc(doc(db, this.collection, clientId), {
        notes: updatedNotes,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to add client note: ${error.message || error}`);
    }
  }

  // Update client notes
  static async updateClientNotes(clientId: string, notes: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to update notes');
      }

      const client = await this.getClient(clientId);
      if (!client) {
        throw new Error('Client not found');
      }

      // Check permissions
      if (client.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new Error('You can only update notes for your own clients');
      }

      await updateDoc(doc(db, this.collection, clientId), {
        notes,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to update client notes: ${error.message || error}`);
    }
  }

  // Update client status
  static async updateClientStatus(clientId: string, status: Client['status']): Promise<void> {
    try {
      const docRef = doc(db, this.collection, clientId);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date(),
      });
    } catch (error) {
      throw new Error(`Failed to update client status: ${error}`);
    }
  }

  // Update client preferences
  static async updateClientPreferences(
    clientId: string,
    preferences: Partial<Client['preferences']>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collection, clientId);
      await updateDoc(docRef, {
        'preferences': preferences,
        updatedAt: new Date(),
      });
    } catch (error) {
      throw new Error(`Failed to update client preferences: ${error}`);
    }
  }

  // Get clients by status
  static async getClientsByStatus(status: Client['status']): Promise<Client[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
    } catch (error) {
      throw new Error(`Failed to get clients by status: ${error}`);
    }
  }

  // Get active clients
  static async getActiveClients(): Promise<Client[]> {
    return this.getClientsByStatus('active' as any);
  }

  // Get leads
  static async getLeads(): Promise<Client[]> {
    return this.getClientsByStatus('lead' as any);
  }

  // Get prospects
  static async getProspects(): Promise<Client[]> {
    return this.getClientsByStatus('prospect' as any);
  }

  // Get clients under contract
  static async getClientsUnderContract(): Promise<Client[]> {
    return this.getClientsByStatus('under_contract' as any);
  }

  // Get closed clients
  static async getClosedClients(): Promise<Client[]> {
    return this.getClientsByStatus('closed' as any);
  }
}
